import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode, useRef } from 'react';
import { 
  Shift, 
  ShiftFilters, 
  JobType, 
  ShiftStatus, 
  ShiftTimeOfDay, 
  CalendarEvent, 
  Department, 
  ShiftStatistics, 
  ShiftDistribution,
  SavedFilter,
  CreateShiftDTO,
  UpdateShiftDTO
} from '../types';
import { shiftService } from '../services/shift.service';
import departmentService from '../services/department.service';
import { format, parseISO } from 'date-fns';
import { ApiError } from '../services/api';
import { shiftToCalendarEvent, generateDummyShifts } from '../utils/shiftUtils';
import { useAuth } from './AuthContext';

// ShiftContext interface
interface ShiftContextType {
  // State
  shifts: Shift[];
  calendarEvents: CalendarEvent[];
  isLoading: boolean;
  error: ApiError | null;
  filters: ShiftFilters;
  selectedDate: Date | null;
  departments: Department[];
  statistics: ShiftStatistics | null;
  distribution: ShiftDistribution | null;
  activeJobTypeFilter: JobType | 'ALL';
  activeTimeOfDayFilter: ShiftTimeOfDay | 'ALL';
  
  // Actions
  setFilters: (filters: ShiftFilters) => void;
  updateFilters: (filters: ShiftFilters) => void;
  setSelectedDate: (date: Date | null) => void;
  setActiveJobTypeFilter: (filter: JobType | 'ALL') => void;
  setActiveTimeOfDayFilter: (filter: ShiftTimeOfDay | 'ALL') => void;
  
  // Operations
  fetchShifts: (overrideFilters?: ShiftFilters) => Promise<void>;
  fetchDepartments: () => Promise<void>;
  fetchStatistics: () => Promise<void>;
  fetchDistribution: () => Promise<void>;
  createShift: (shiftData: CreateShiftDTO) => Promise<Shift>;
  updateShift: (id: string, shiftData: UpdateShiftDTO) => Promise<Shift>;
  deleteShift: (id: string) => Promise<void>;
  cancelShift: (id: string, reason: string) => Promise<void>;
  updateShiftStatus: (id: string, status: ShiftStatus) => Promise<Shift>;
  updateShiftBatch: (ids: string[], status: ShiftStatus) => Promise<{ count: number }>;
  calculateShiftMetrics: (data: Partial<CreateShiftDTO>) => Promise<{ totalHours: number, totalPay: number, totalShifts: number }>;
  rescheduleShift: (id: string, startTime: string, endTime: string) => Promise<Shift>;
  
  // Saved filters
  fetchSavedFilters: () => Promise<SavedFilter[]>;
  saveCurrentFilter: (name: string) => Promise<SavedFilter>;
  deleteSavedFilter: (id: string) => Promise<void>;
}

// Provider props
interface ShiftProviderProps {
  children: ReactNode;
}

// Create context
const ShiftContext = createContext<ShiftContextType | undefined>(undefined);

// Convert shifts to calendar events
const convertShiftsToEvents = (shifts: Shift[]): CalendarEvent[] => {
  return shifts.map(shift => {
    // Set color based on status
    let backgroundColor = '#3788d8'; // Default blue (open)
    let borderColor = '#3788d8';

    if (shift.status === ShiftStatus.BOOKED) {
      backgroundColor = '#28a745'; // Green (booked)
      borderColor = '#28a745';
    } else if (shift.status === ShiftStatus.CANCELLED) {
      backgroundColor = '#dc3545'; // Red (cancelled)
      borderColor = '#dc3545';
    } else if (shift.status === ShiftStatus.COMPLETED) {
      backgroundColor = '#6c757d'; // Gray (completed)
      borderColor = '#6c757d';
    }

    // Format times for display
    const formattedStartTime = format(parseISO(shift.startTime), 'HH:mm');
    const formattedEndTime = format(parseISO(shift.endTime), 'HH:mm');

    return {
      id: shift.id,
      title: `${shift.jobType} ${formattedStartTime}-${formattedEndTime}`,
      start: shift.startTime,
      end: shift.endTime,
      extendedProps: {
        shift: shift
      },
      backgroundColor,
      borderColor,
      textColor: '#ffffff'
    };
  });
};

// Provider component
export const ShiftProvider: React.FC<ShiftProviderProps> = ({ children }) => {
  // Get user/hospital info from AuthContext
  const { hospital } = useAuth();
  
  // State
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [statistics, setStatistics] = useState<ShiftStatistics | null>(null);
  const [distribution, setDistribution] = useState<ShiftDistribution | null>(null);
  
  // 필터 변경 감지용 isFirstRender ref - 컴포넌트 최상위 레벨에서 선언
  const isFirstRender = useRef(true);
  
  // Filters
  const [activeJobTypeFilter, setActiveJobTypeFilter] = useState<JobType | 'ALL'>('ALL');
  const [activeTimeOfDayFilter, setActiveTimeOfDayFilter] = useState<ShiftTimeOfDay | 'ALL'>('ALL');
  const [filters, setFilters] = useState<ShiftFilters>({
    status: undefined,
    jobType: undefined,
    timeType: undefined,
    startDate: undefined,
    endDate: undefined,
    page: 1,
    limit: 100
  });

  // Memoize the combined filters for API calls
  const apiFilters = useMemo(() => {
    const combinedFilters: ShiftFilters = { ...filters };
    
    // Apply job type filter if not 'ALL'
    if (activeJobTypeFilter !== 'ALL') {
      combinedFilters.jobType = activeJobTypeFilter;
    }
    
    // Apply time of day filter if not 'ALL'
    if (activeTimeOfDayFilter !== 'ALL') {
      combinedFilters.timeType = activeTimeOfDayFilter;
    }
    
    return combinedFilters;
  }, [filters, activeJobTypeFilter, activeTimeOfDayFilter]);

  // Fetch departments
  const fetchDepartments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // 로그인한 병원 ID 사용
      const hospitalId = hospital?.id || localStorage.getItem('hospitalId');
      
      if (!hospitalId) {
        console.error('Hospital ID not found for department fetch');
        return;
      }
      
      console.log(`Fetching departments for hospital: ${hospitalId}`);
      const response = await departmentService.getHospitalDepartments(hospitalId);
      
      if (response && response.departments) {
        setDepartments(response.departments);
      }
    } catch (err) {
      setError(err as ApiError);
      console.error('Error fetching departments:', err);
    } finally {
      setIsLoading(false);
    }
  }, [hospital]);
  
  // Fetch shifts
  const fetchShifts = useCallback(async (overrideFilters?: ShiftFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const filtersToUse = overrideFilters || apiFilters;
      
      // 로그인한 병원 ID 사용 - AuthContext에서 가져옴
      const hospitalId = hospital?.id || localStorage.getItem('hospitalId');
      
      // 병원 ID가 없으면 더미 데이터 사용
      if (!hospitalId) {
        console.error('Hospital ID not found, using dummy data');
        const dummyShifts = generateDummyShifts(20, {
          hospitalId: 'test-hospital-id',
          startDate: new Date(),
          endDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
        });
        setShifts(dummyShifts);
        setCalendarEvents(convertShiftsToEvents(dummyShifts));
        return;
      }
      
      console.log(`Fetching shifts for hospital: ${hospitalId}`);
      const response = await shiftService.getHospitalShifts(hospitalId, filtersToUse);
      
      if (response && response.shifts && response.shifts.length > 0) {
        setShifts(response.shifts);
        setCalendarEvents(convertShiftsToEvents(response.shifts));
      } else {
        console.log('No shifts from API, generating local dummy data');
        const localDummyShifts = generateDummyShifts(20, {
          hospitalId: hospitalId,
          startDate: new Date(),
          endDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
        });
        setShifts(localDummyShifts);
        setCalendarEvents(convertShiftsToEvents(localDummyShifts));
      }
    } catch (err) {
      setError(err as ApiError);
      console.error('Error fetching shifts:', err);
      
      // 에러 발생 시 fallback - 사용자 경험 개선
      const hospitalId = hospital?.id || localStorage.getItem('hospitalId') || 'test-hospital-id';
      const fallbackShifts = generateDummyShifts(10, {
        hospitalId: hospitalId,
        startDate: new Date(),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
      });
      setShifts(fallbackShifts);
      setCalendarEvents(convertShiftsToEvents(fallbackShifts));
    } finally {
      setIsLoading(false);
    }
  // 의존성 배열에 hospital 추가
  }, [apiFilters, hospital]);

  // 필터 변경 감지용 Effect 개선 - debounce 패턴 적용으로 불필요한 API 호출 방지
  const loadDataOnFilterChange = useCallback(async () => {
    try {
      await fetchShifts(apiFilters);
    } catch (error) {
      console.error('Failed to load data on filter change:', error);
    }
  }, [apiFilters, fetchShifts]);

  // debounce 타이머 ref 추가
  const filterChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // 컴포넌트가 마운트될 때 한 번만 실행되는 플래그 사용
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // 디바운스 처리 - 300ms 내에 여러 번 변경이 발생하면 마지막 한 번만 API 호출
    if (filterChangeTimerRef.current) {
      clearTimeout(filterChangeTimerRef.current);
    }
    
    filterChangeTimerRef.current = setTimeout(() => {
      // isLoading 체크 제거 - 타이머가 실행될 때는 무조건 한 번 loadData 함수 실행
      loadDataOnFilterChange();
    }, 300);
    
    return () => {
      if (filterChangeTimerRef.current) {
        clearTimeout(filterChangeTimerRef.current);
      }
    };
  // loadDataOnFilterChange만 의존성으로 포함
  }, [loadDataOnFilterChange]);

  // 초기 데이터 로딩 로직 개선
  const initialLoad = useCallback(async () => {
    try {
      await fetchDepartments();
      
      // 이미 데이터가 있는지 확인하고 없을 때만 로드
      if (shifts.length === 0) {
        await fetchShifts();
      }
    } catch (error) {
      console.error('Initial data loading failed:', error);
    }
  }, [fetchDepartments, fetchShifts, shifts.length]);

  // 첫 로딩 및 주기적 업데이트를 위한 Effect 개선
  useEffect(() => {
    let isMounted = true;
    let refreshInterval: ReturnType<typeof setInterval>;
    
    // 초기 데이터 로딩
    const loadInitialData = async () => {
      if (isMounted) {
        await initialLoad();
      }
    };
    
    loadInitialData();
    
    // 주기적 데이터 갱신 (5분마다) - 이전 요청이 완료된 상태일 때만 실행되도록 최적화
    let isRefreshing = false;
    refreshInterval = setInterval(async () => {
      if (!isRefreshing && isMounted) {
        isRefreshing = true;
        await fetchShifts();
        isRefreshing = false;
      }
    }, 5 * 60 * 1000);

    // cleanup
    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
    };
  // 의존성 배열에는 initialLoad만 포함 - 불필요한 재실행 방지
  }, [initialLoad]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const hospitalId = hospital?.id || localStorage.getItem('hospitalId');
      
      if (!hospitalId) {
        console.error('Hospital ID not found for statistics fetch');
        return;
      }
      
      const stats = await shiftService.getShiftStatistics(hospitalId, apiFilters);
      setStatistics(stats);
    } catch (err) {
      setError(err as ApiError);
      console.error('Error fetching statistics:', err);
      
      // API 호출 실패 시 기본 통계 데이터 설정 - ShiftStatistics 타입에 맞춤
      const defaultStats: ShiftStatistics = {
        totalShifts: 0,
        openShifts: 0,
        bookedShifts: 0,
        completedShifts: 0,
        cancelledShifts: 0,
        averageHourlyRate: 0
      };
      
      setStatistics(defaultStats);
    } finally {
      setIsLoading(false);
    }
  }, [apiFilters, hospital]);

  // Fetch distribution data
  const fetchDistribution = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const hospitalId = hospital?.id || localStorage.getItem('hospitalId');
      
      if (!hospitalId) {
        console.error('Hospital ID not found for distribution fetch');
        return;
      }
      
      const dist = await shiftService.getShiftDistribution(hospitalId, apiFilters);
      setDistribution(dist);
    } catch (err) {
      setError(err as ApiError);
      console.error('Error fetching distribution:', err);
      
      // API 호출 실패 시 기본 분포 데이터 설정 - ShiftDistribution 타입에 맞춤
      const defaultDistribution: ShiftDistribution = {
        byJobType: {},
        byTimeType: {},
        byDepartment: {},
        byStatus: {}
      };
      
      setDistribution(defaultDistribution);
    } finally {
      setIsLoading(false);
    }
  }, [apiFilters, hospital]);

  // Fetch saved filters
  const fetchSavedFilters = useCallback(async (): Promise<SavedFilter[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await shiftService.getSavedFilters();
      return response.savedFilters;
    } catch (err) {
      setError(err as ApiError);
      console.error('Error fetching saved filters:', err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save current filter
  const saveCurrentFilter = useCallback(async (name: string): Promise<SavedFilter> => {
    setIsLoading(true);
    setError(null);
    try {
      return await shiftService.saveFilter(name, apiFilters);
    } catch (err) {
      setError(err as ApiError);
      console.error('Error saving filter:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiFilters]);

  // Delete saved filter
  const deleteSavedFilter = useCallback(async (id: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await shiftService.deleteFilter(id);
    } catch (err) {
      setError(err as ApiError);
      console.error('Error deleting filter:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create shift - 최적화
  const createShift = useCallback(async (shiftData: CreateShiftDTO): Promise<Shift> => {
    setIsLoading(true);
    setError(null);
    try {
      // 로컬 유효성 검사로 불필요한 API 호출 방지
      if (!shiftData.hospitalId) {
        throw { message: '병원 ID가 필요합니다', statusCode: 400 } as ApiError;
      }
      
      const newShift = await shiftService.createShift(shiftData);
      
      // 로컬 상태 직접 업데이트 - 불필요한 API 요청 방지
      setShifts(prevShifts => {
        // 중복 방지 체크
        if (prevShifts.some(s => s.id === newShift.id)) {
          return prevShifts; // 이미 존재하는 경우 상태 변경 없음
        }
        return [...prevShifts, newShift];
      });
      
      // 캘린더 이벤트도 직접 업데이트 (불필요한 API 호출 방지)
      setCalendarEvents(prevEvents => {
        const newEvent = convertShiftsToEvents([newShift])[0];
        // 중복 방지 체크
        if (prevEvents.some(e => e.id === newEvent.id)) {
          return prevEvents; // 이미 존재하는 경우 상태 변경 없음
        }
        return [...prevEvents, newEvent];
      });
      
      return newShift;
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError);
      console.error('Error creating shift:', apiError);
      throw apiError;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update shift - 최적화
  const updateShift = useCallback(async (id: string, shiftData: UpdateShiftDTO): Promise<Shift> => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedShift = await shiftService.updateShift(id, shiftData);
      
      // 로컬 상태 직접 업데이트 - 불필요한 API 요청 방지
      setShifts(prevShifts => {
        return prevShifts.map(shift => 
          shift.id === id ? updatedShift : shift
        );
      });
      
      // 캘린더 이벤트도 직접 업데이트
      setCalendarEvents(prevEvents => {
        const updatedEvent = convertShiftsToEvents([updatedShift])[0];
        return prevEvents.map(event => 
          event.id === id ? updatedEvent : event
        );
      });
      
      return updatedShift;
    } catch (err) {
      setError(err as ApiError);
      console.error('Error updating shift:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update shift status - 최적화
  const updateShiftStatus = useCallback(async (id: string, status: ShiftStatus): Promise<Shift> => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedShift = await shiftService.updateShiftStatus(id, status);
      
      // 로컬 상태 직접 업데이트
      setShifts(prevShifts => {
        return prevShifts.map(shift => 
          shift.id === id ? updatedShift : shift
        );
      });
      
      // 캘린더 이벤트도 직접 업데이트
      setCalendarEvents(prevEvents => {
        const updatedEvent = convertShiftsToEvents([updatedShift])[0];
        return prevEvents.map(event => 
          event.id === id ? updatedEvent : event
        );
      });
      
      return updatedShift;
    } catch (err) {
      setError(err as ApiError);
      console.error('Error updating shift status:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Batch update shift statuses
  const updateShiftBatch = useCallback(async (ids: string[], status: ShiftStatus): Promise<{ count: number }> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await shiftService.updateShiftStatusBatch(ids, status);
      
      // fetchShifts 호출 대신 로컬 상태 직접 업데이트
      setShifts(prevShifts => {
        return prevShifts.map(shift => 
          ids.includes(shift.id) 
            ? { ...shift, status: status } 
            : shift
        );
      });
      
      // 캘린더 이벤트도 직접 업데이트
      setCalendarEvents(prevEvents => {
        return prevEvents.map(event => {
          if (ids.includes(event.id)) {
            // 상태에 따른 색상 설정
            let backgroundColor = '#3788d8'; // Default blue (open)
            let borderColor = '#3788d8';
            
            if (status === ShiftStatus.BOOKED) {
              backgroundColor = '#28a745'; // Green (booked)
              borderColor = '#28a745';
            } else if (status === ShiftStatus.CANCELLED) {
              backgroundColor = '#dc3545'; // Red (cancelled)
              borderColor = '#dc3545';
            } else if (status === ShiftStatus.COMPLETED) {
              backgroundColor = '#6c757d'; // Gray (completed)
              borderColor = '#6c757d';
            }
            
            return {
              ...event,
              backgroundColor,
              borderColor,
              extendedProps: {
                ...event.extendedProps,
                shift: {
                  ...event.extendedProps.shift,
                  status: status
                }
              }
            };
          }
          return event;
        });
      });
      
      return result;
    } catch (err) {
      setError(err as ApiError);
      console.error('Error updating shift batch:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Cancel shift - 최적화
  const cancelShift = useCallback(async (id: string, reason: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await shiftService.updateShiftStatus(id, ShiftStatus.CANCELLED);
      
      // 로컬 상태 직접 업데이트
      setShifts(prevShifts => {
        return prevShifts.map(shift => {
          if (shift.id === id) {
            return { 
              ...shift, 
              status: ShiftStatus.CANCELLED,
              cancelReason: reason
            };
          }
          return shift;
        });
      });
      
      // 캘린더 이벤트도 직접 업데이트
      setCalendarEvents(prevEvents => {
        return prevEvents.map(event => {
          if (event.id === id) {
            // 취소 상태용 색상 설정
            const cancelColor = '#dc3545'; // red
            return {
              ...event,
              backgroundColor: cancelColor,
              borderColor: cancelColor,
              extendedProps: {
                ...event.extendedProps,
                shift: {
                  ...event.extendedProps.shift,
                  status: ShiftStatus.CANCELLED,
                  cancelReason: reason
                }
              }
            };
          }
          return event;
        });
      });
    } catch (err) {
      setError(err as ApiError);
      console.error('Error cancelling shift:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Calculate shift metrics
  const calculateShiftMetrics = useCallback(async (
    data: Partial<CreateShiftDTO>
  ): Promise<{ totalHours: number, totalPay: number, totalShifts: number }> => {
    try {
      return await shiftService.calculateShiftMetrics(data);
    } catch (err) {
      console.error('Error calculating shift metrics:', err);
      throw err;
    }
  }, []);

  // Reschedule shift
  const rescheduleShift = useCallback(async (id: string, startTime: string, endTime: string): Promise<Shift> => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedShift = await shiftService.rescheduleShift(id, startTime, endTime);
      
      // 로컬 상태 직접 업데이트
      setShifts(prevShifts => {
        return prevShifts.map(shift => {
          if (shift.id === id) {
            return { ...shift, ...updatedShift };
          }
          return shift;
        });
      });
      
      // 캘린더 이벤트도 직접 업데이트
      setCalendarEvents(prevEvents => {
        return prevEvents.map(event => {
          if (event.id === id) {
            return {
              ...event,
              start: updatedShift.startTime,
              end: updatedShift.endTime,
              extendedProps: {
                ...event.extendedProps,
                shift: updatedShift
              }
            };
          }
          return event;
        });
      });
      
      return updatedShift;
    } catch (err) {
      setError(err as ApiError);
      console.error('Error rescheduling shift:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete shift
  const deleteShift = useCallback(async (id: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await shiftService.deleteShift(id);
      
      // 로컬 상태 직접 업데이트
      setShifts(prevShifts => {
        return prevShifts.filter(shift => shift.id !== id);
      });
      
      // 캘린더 이벤트도 직접 업데이트
      setCalendarEvents(prevEvents => {
        return prevEvents.filter(event => event.id !== id);
      });
    } catch (err) {
      setError(err as ApiError);
      console.error('Error deleting shift:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Return context value
  const contextValue = useMemo<ShiftContextType>(() => {
    return {
      // State
      shifts,
      calendarEvents,
      isLoading,
      error,
      filters,
      selectedDate,
      departments,
      statistics,
      distribution,
      activeJobTypeFilter,
      activeTimeOfDayFilter,
      
      // Actions
      setFilters,
      updateFilters: setFilters,
      setSelectedDate,
      setActiveJobTypeFilter,
      setActiveTimeOfDayFilter,
      
      // Operations
      fetchShifts,
      fetchDepartments,
      fetchStatistics,
      fetchDistribution,
      createShift,
      updateShift,
      deleteShift,
      cancelShift,
      updateShiftStatus,
      updateShiftBatch,
      calculateShiftMetrics,
      rescheduleShift,
      
      // Saved filters
      fetchSavedFilters,
      saveCurrentFilter,
      deleteSavedFilter
    };
  }, [
    shifts, 
    calendarEvents, 
    isLoading, 
    error, 
    filters, 
    selectedDate,
    departments,
    statistics,
    distribution,
    activeJobTypeFilter,
    activeTimeOfDayFilter,
    setFilters,
    setSelectedDate,
    setActiveJobTypeFilter,
    setActiveTimeOfDayFilter,
    fetchShifts,
    fetchDepartments,
    fetchStatistics,
    fetchDistribution,
    createShift,
    updateShift,
    deleteShift,
    cancelShift,
    updateShiftStatus,
    updateShiftBatch,
    calculateShiftMetrics,
    rescheduleShift,
    fetchSavedFilters,
    saveCurrentFilter,
    deleteSavedFilter
  ]);

  return (
    <ShiftContext.Provider value={contextValue}>
      {children}
    </ShiftContext.Provider>
  );
};

// Custom hook to use the shifts context
export const useShifts = (): ShiftContextType => {
  const context = useContext(ShiftContext);
  if (context === undefined) {
    throw new Error('useShifts must be used within a ShiftProvider');
  }
  return context;
}; 